import React, { useMemo } from "react";
import { formatINR } from "@/mock";

// Renders an interactive seat map for one flight segment.
// seats: [{seatNo,row,col,booked,window,aisle,legroom,amount}]
export const SeatMap = ({ segment, seats, selectedSeatNo, onSelect }) => {
  const { rows, cols, byRow } = useMemo(() => {
    const maxRow = Math.max(1, ...seats.map((s) => s.row || 0));
    const maxCol = Math.max(1, ...seats.map((s) => s.col || 0));
    const grid = {};
    seats.forEach((s) => {
      if (!grid[s.row]) grid[s.row] = {};
      grid[s.row][s.col] = s;
    });
    return { rows: maxRow, cols: maxCol, byRow: grid };
  }, [seats]);

  const aisleAfterCol = Math.ceil(cols / 2); // gap in the middle

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm font-bold text-slate-800">
          {segment.from} ({segment.fromCode}) → {segment.to} ({segment.toCode})
        </p>
        <span className="text-[11px] font-semibold text-slate-400">{segment.flightNo}</span>
      </div>

      {/* legend */}
      <div className="mb-3 flex flex-wrap gap-3 text-[11px] text-slate-500">
        <span className="flex items-center gap-1"><i className="h-3 w-3 rounded-sm border border-emerald-300 bg-emerald-50" /> Free</span>
        <span className="flex items-center gap-1"><i className="h-3 w-3 rounded-sm border border-amber-300 bg-amber-50" /> Paid</span>
        <span className="flex items-center gap-1"><i className="h-3 w-3 rounded-sm border border-slate-300 bg-slate-200" /> Booked</span>
        <span className="flex items-center gap-1"><i className="h-3 w-3 rounded-sm border border-[#1B1F5B] bg-[#1B1F5B]" /> Selected</span>
      </div>

      <div className="flex justify-center overflow-x-auto pb-2">
        <div className="inline-block rounded-2xl bg-slate-50 p-3">
          <div className="mb-2 rounded-t-full border-b-2 border-dashed border-slate-300 py-1 text-center text-[10px] font-bold uppercase tracking-widest text-slate-400">
            Front
          </div>
          {Array.from({ length: rows }, (_, r) => r + 1).map((row) => (
            <div key={row} className="flex items-center gap-1.5 py-0.5">
              <span className="w-5 text-right text-[10px] font-semibold text-slate-400">{row}</span>
              {Array.from({ length: cols }, (_, c) => c + 1).map((col) => {
                const seat = byRow[row]?.[col];
                const gap = col === aisleAfterCol ? "mr-4" : "";
                if (!seat) return <span key={col} className={`h-7 w-7 ${gap}`} data-testid={`seat-empty-${row}-${col}`} />;
                const isSel = seat.seatNo === selectedSeatNo;
                const cls = seat.booked
                  ? "cursor-not-allowed border-slate-300 bg-slate-200 text-slate-400"
                  : isSel
                  ? "border-[#1B1F5B] bg-[#1B1F5B] text-white"
                  : seat.amount > 0
                  ? "border-amber-300 bg-amber-50 text-amber-700 hover:border-amber-500"
                  : "border-emerald-300 bg-emerald-50 text-emerald-700 hover:border-emerald-500";
                return (
                  <button
                    key={col}
                    type="button"
                    disabled={seat.booked}
                    onClick={() => onSelect(seat)}
                    title={`${seat.seatNo}${seat.amount > 0 ? " · " + formatINR(seat.amount) : " · Free"}${seat.window ? " · Window" : seat.aisle ? " · Aisle" : ""}`}
                    data-testid={`seat-${seat.seatNo}`}
                    className={`h-7 w-7 rounded-md border text-[9px] font-bold transition-colors ${cls} ${gap}`}
                  >
                    {seat.seatNo?.replace(/^\d+/, "")}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SeatMap;
